import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBootstrap } from '../../context/BootstrapContext';
import { getExperimentosPagedRaw } from '../../infra/api/ExperimentoApi';
import { listarPaginasColeta } from '../../infra/api/Coletas';
import { usePermissao } from '../../security/usePermissao';
import { usePerfilPlano } from '../../security/usePerfilPlano';
import { GraficoTipo } from '../../infra/api/GraficoApi';
import { useCharts } from '../../hooks/useCharts';
import {
    Box,
    Stack,
    Typography,
    Button,
    CircularProgress,
} from '@mui/material';
import { SnackbarAlert } from '../../components/SnackbarAlert';

import ExperimentHeader from './components/ExperimentHeader';
import ChartTabNavigation, { TABS, TIPOS_AMOSTRA } from './components/ChartTabNavigation';
import { abasNaOrdemDoPlano } from './utils/ordemAbas';
import SpectralChartSection from './components/SpectralChartSection';
import {
    SPECTRAL_CHART_TYPES,
    detectSensorType,
} from './utils/sensorDetection';
import {
    downloadAllCharts,
    downloadAllChartsCsv,
    downloadAllChartsXlsx,
} from './utils/chartDownload';

/**
 * Nomes do protocolo e da análise para o cabeçalho, a partir do bootstrap. O
 * experimento vem cru do backend (ou do estado da navegação) e só tem os IDs.
 */
const comNomesDoProtocolo = (experimento, protocolos) => {
    if (!experimento) return null;
    const protocolo = (protocolos || []).find((p) => p.id === experimento.protocolId);
    const analise =
        protocolo?.analysis?.find((a) => a.id === experimento.analysisId) || protocolo?.analysis?.[0];
    return {
        ...experimento,
        protocolName: experimento.protocolName || protocolo?.name || 'Protocolo desconhecido',
        analysisName: experimento.analysisName || analise?.name || 'Análise não encontrada',
        protocolColor: experimento.protocolColor || protocolo?.color,
    };
};

export default function GraficoTabsPage() {
    const { tenant, experimentId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const { bootstrap } = useBootstrap();
    const { pode } = usePermissao();

    // Quem abriu os gráficos diz para onde voltar (Amostras ou o detalhe de uma coleta).
    const voltarPara = location.state?.voltarPara || `/${tenant}/amostras`;

    // Só o experimento aberto: quando a navegação já o traz, nenhuma requisição; num F5,
    // busca pelo id. Antes a rota carregava TODOS os experimentos da conta para achar um.
    const [experimentoBruto, setExperimentoBruto] = useState(location.state?.experiment || null);
    const [carregandoExperimento, setCarregandoExperimento] = useState(!location.state?.experiment);

    useEffect(() => {
        if (location.state?.experiment) return undefined;
        let ativo = true;
        setCarregandoExperimento(true);
        getExperimentosPagedRaw({ id: experimentId, size: 1 })
            .then((pagina) => ativo && setExperimentoBruto(pagina?.items?.[0] || null))
            .catch(() => ativo && setExperimentoBruto(null))
            .finally(() => ativo && setCarregandoExperimento(false));
        return () => {
            ativo = false;
        };
    }, [experimentId, location.state]);

    // Bloqueia hard-navigation para quem nao tem a tela de graficos completa
    const canViewFullCharts = pode('graficos.completos');

    const experiment = useMemo(
        () => comNomesDoProtocolo(experimentoBruto, bootstrap?.protocolos),
        [experimentoBruto, bootstrap?.protocolos]
    );

    // Sem os parametros da torre, so os graficos da AMOSTRA (ver capacidades.js).
    const verParametrosTorre = pode('graficos.parametrosTorre');
    const { frequenciaPrimeiro } = usePerfilPlano();
    const tabsVisiveis = useMemo(
        () =>
            abasNaOrdemDoPlano(verParametrosTorre ? TABS : TABS.filter((t) => TIPOS_AMOSTRA.has(t.key)), {
                frequenciaPrimeiro,
            }),
        [verParametrosTorre, frequenciaPrimeiro]
    );

    // Experimento da análise configurada na página do LEITE: o download leva também a
    // frequência ressonante (o sinal do modelo de CBT). Colostro e água não levam.
    const [ehLeite, setEhLeite] = useState(false);
    useEffect(() => {
        const analiseId = experiment?.analysisId;
        if (!analiseId) return undefined;
        let ativo = true;
        listarPaginasColeta()
            .then((lista) => ativo && setEhLeite((lista || []).some((c) => c.pagina === 'LEITE' && c.analiseId === analiseId)))
            .catch(() => ativo && setEhLeite(false));
        return () => {
            ativo = false;
        };
    }, [experiment?.analysisId]);
    const opcoesDownload = useMemo(() => ({ incluirFrequencia: ehLeite }), [ehLeite]);

    // Abre na primeira aba do plano (no LEITE, Frequências).
    const [activeTab, setActiveTab] = useState(() => tabsVisiveis[0]?.key || TABS[0].key);
    const [isDownloading, setIsDownloading] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });

    const avisar = useCallback(
        (message, severity = 'error') => setSnack({ open: true, message, severity }),
        []
    );

    // A aba ativa pode apontar para um grafico que este papel nao ve — o papel muda, ou
    // o usuario volta a uma pagina que ficou aberta. Sem isto a tela ficaria em branco
    // sem erro nenhum, e ainda dispararia um POST /grafico de tipo proibido.
    useEffect(() => {
        if (!tabsVisiveis.length) return;
        if (!tabsVisiveis.some((t) => t.key === activeTab)) {
            setActiveTab(tabsVisiveis[0].key);
        }
    }, [tabsVisiveis, activeTab]);

    const {
        charts,
        errors,
        errorCodes,
        loading: loadingCharts,
        load,
    } = useCharts(experimentId);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, []);

    // [SEGURANÇA] Route Guard redirect: empurra para fora utilizadores não autorizados
    useEffect(() => {
        if (!carregandoExperimento && experiment && !canViewFullCharts) {
            navigate(voltarPara, { replace: true });
        }
    }, [carregandoExperimento, experiment, canViewFullCharts, navigate, voltarPara]);

    useEffect(() => {
        if (activeTab && canViewFullCharts) load(activeTab);
    }, [activeTab, load, canViewFullCharts]);

    useEffect(() => {
        if (
            activeTab === GraficoTipo.SPECTRAL_NEPHELOMETRY &&
            canViewFullCharts
        ) {
            load(GraficoTipo.SPECTRAL_TURBIDIMETRY);
        }
    }, [activeTab, load, canViewFullCharts]);

    const handleDownload = useCallback(async () => {
        if (!experimentId) return;
        try {
            setIsDownloading(true);
            await downloadAllCharts(experimentId, experiment, opcoesDownload);
        } catch (err) {
            console.error('Erro fatal ao gerar download:', err);
            avisar('Erro ao processar o download dos dados.');
        } finally {
            setIsDownloading(false);
        }
    }, [experimentId, experiment, opcoesDownload, avisar]);

    const handleDownloadCsv = useCallback(async () => {
        if (!experimentId) return;
        try {
            setIsDownloading(true);
            await downloadAllChartsCsv(experimentId, experiment, opcoesDownload);
        } catch (err) {
            console.error('Erro fatal ao gerar download CSV:', err);
            avisar('Erro ao processar o download CSV dos dados.');
        } finally {
            setIsDownloading(false);
        }
    }, [experimentId, experiment, opcoesDownload, avisar]);

    const handleDownloadXlsx = useCallback(async () => {
        if (!experimentId) return;
        try {
            setIsDownloading(true);
            await downloadAllChartsXlsx(experimentId, experiment, opcoesDownload);
        } catch (err) {
            console.error('Erro fatal ao gerar download XLSX:', err);
            avisar('Erro ao processar o download Excel dos dados.');
        } finally {
            setIsDownloading(false);
        }
    }, [experimentId, experiment, opcoesDownload, avisar]);

    if (carregandoExperimento && !experiment) {
        return (
            <Box
                sx={{
                    p: { xs: 1.5, md: 2 },
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80vh',
                }}
            >
                <Stack spacing={2} alignItems="center">
                    <CircularProgress />
                    <Typography>
                        Carregando o experimento...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (!experiment) {
        return (
            <Box
                sx={{
                    p: { xs: 1.5, md: 2 },
                    width: '100%',
                    textAlign: 'center',
                    mt: 4,
                }}
            >
                <Typography variant="h6" color="error" gutterBottom>
                    Experimento não encontrado
                </Typography>
                <Typography color="text.secondary">
                    O ID pode estar incorreto ou o experimento não está mais
                    disponível.
                </Typography>
                <Button
                    variant="contained"
                    sx={{ mt: 3 }}
                    onClick={() => navigate(voltarPara)}
                >
                    Voltar
                </Button>
            </Box>
        );
    }

    // Previne flickering de interface indesejada antes do redirecionamento
    if (!canViewFullCharts) {
        return null;
    }

    const isSpectral = SPECTRAL_CHART_TYPES.has(activeTab);
    const chartData = charts[activeTab];
    const activeLabel =
        TABS.find((t) => t.key === activeTab)?.label || 'Gráfico';
    const sensorType = detectSensorType(activeTab, experiment, chartData);

    const turbidimetryChart =
        activeTab === GraficoTipo.SPECTRAL_NEPHELOMETRY ||
        activeTab === GraficoTipo.SPECTRAL_FLUORESCENCE
            ? charts[GraficoTipo.SPECTRAL_TURBIDIMETRY]
            : null;

    return (
        // OTIMIZAÇÃO DE ESPAÇO: Box a usar width 100% no lugar de Container
        <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 }, width: '100%' }}>
            <ExperimentHeader
                experiment={experiment}
                experimentId={experimentId}
                onBack={() => navigate(voltarPara)}
            />

            <ChartTabNavigation
                tabs={tabsVisiveis}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onRefresh={() => load(activeTab, { force: true })}
                onDownload={handleDownload}
                onDownloadCsv={handleDownloadCsv}
                onDownloadXlsx={handleDownloadXlsx}
                loadingCharts={loadingCharts}
                isDownloading={isDownloading}
            />

            <Box sx={{ mt: { xs: 2, md: 3 } }}>
                <SpectralChartSection
                    activeTab={activeTab}
                    chartData={chartData}
                    error={errors[activeTab]}
                    errorCode={errorCodes[activeTab]}
                    onRetry={() => load(activeTab, { force: true })}
                    sensorType={sensorType}
                    turbidimetryChart={turbidimetryChart}
                    experiment={experiment}
                    activeLabel={activeLabel}
                    loading={loadingCharts}
                    isSpectral={isSpectral}
                />
            </Box>

            <SnackbarAlert
                open={snack.open}
                onClose={() => setSnack((p) => ({ ...p, open: false }))}
                message={snack.message}
                severity={snack.severity}
            />
        </Box>
    );
}
